import { Component, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '../../shared/auth.service';

@Component({
  standalone: true,
  selector: 'app-login-page',
  imports: [CommonModule, FormsModule],
  template: `
  <div class="page">
    <div class="card">
      <div class="hero">
        <div class="brand">
          <img class="brandImg" src="assets/omnifai-logo.png" alt="OmnifAI logo" />
          <div>
            <div class="title">OmnifAI</div>
          </div>
        </div>
        <div class="tagline">Unified inbox for leads, orders, and human-assist operations</div>
      </div>

      <h1>Sign In</h1>

      <form class="form" (ngSubmit)="signIn()">
        <label>Chatbot Number (Username)</label>
        <input
          [(ngModel)]="orgId"
          name="username"
          type="text"
          placeholder="Enter chatbot number including country code"
        />

        <label>Password</label>
        <div class="passwordWrap">
          <input
            [(ngModel)]="password"
            name="password"
            [type]="showPassword() ? 'text' : 'password'"
            placeholder="Enter password"
          />
          <button
            type="button"
            class="eyeBtn"
            [attr.aria-label]="showPassword() ? 'Hide password' : 'Show password'"
            (click)="togglePasswordVisibility()"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6z" fill="none" stroke="currentColor" stroke-width="1.8"/>
              <circle cx="12" cy="12" r="3.2" fill="none" stroke="currentColor" stroke-width="1.8"/>
              <path *ngIf="showPassword()" d="M4 20L20 4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
            </svg>
          </button>
        </div>

        <button type="submit" class="btn primary" [disabled]="loading()">{{ loading() ? 'Signing In...' : 'Sign In' }}</button>
      </form>

      <div class="msg ok" *ngIf="success()">{{ success() }}</div>
      <div class="msg err" *ngIf="error()">{{ error() }}</div>
    </div>
  </div>
  `,
  styles: [`
    .page{
      min-height:100vh;display:grid;place-items:center;padding:20px;
      background:
        radial-gradient(900px 480px at 10% 0%, rgba(0,168,132,.14), transparent 62%),
        radial-gradient(900px 480px at 100% 0%, rgba(6,182,212,.12), transparent 56%),
        var(--bg);
    }
    .card{
      width:min(460px,calc(100vw - 24px));
      border:1px solid var(--border);
      background:rgba(17,27,33,.88);
      border-radius:20px;padding:18px;
      box-shadow:0 26px 60px rgba(2,8,23,.28);
    }
    .hero{
      border:1px solid rgba(45,212,191,.22);
      background:
        radial-gradient(560px 180px at -8% -24%, rgba(45,212,191,.16), transparent 58%),
        radial-gradient(500px 180px at 108% -30%, rgba(59,130,246,.14), transparent 56%),
        rgba(15,23,42,.45);
      border-radius:16px;
      padding:12px;
      margin-bottom:8px;
    }
    .brand{display:flex;gap:12px;align-items:center}
    .brandImg{width:48px;height:48px;object-fit:cover;border-radius:14px;flex:0 0 auto}
    .title{font-weight:900;letter-spacing:.3px}
    .tagline{
      margin-top:10px;
      font-size:12px;
      color:var(--muted);
      line-height:1.35;
    }
    h1{margin:14px 0 10px;font-size:20px}
    .form{display:grid;gap:8px}
    label{font-size:12px;color:var(--muted)}
    input{
      height:40px;border-radius:11px;border:1px solid var(--border);background:var(--input);
      color:var(--text);padding:0 10px;outline:none
    }
    .passwordWrap{position:relative}
    .passwordWrap input{width:100%;padding-right:42px}
    .eyeBtn{
      position:absolute;right:8px;top:50%;transform:translateY(-50%);
      width:28px;height:28px;border:0;background:transparent;color:var(--muted);cursor:pointer;padding:0;
      display:grid;place-items:center;
    }
    .eyeBtn svg{width:18px;height:18px}
    .eyeBtn:hover{color:var(--text)}
    .btn{
      margin-top:8px;height:38px;border-radius:10px;border:1px solid var(--border);background:transparent;
      color:var(--text);cursor:pointer
    }
    .btn.primary{border-color:rgba(0,168,132,.45);background:rgba(0,168,132,.18)}
    .msg{margin-top:12px;font-size:12px;padding:8px 10px;border-radius:10px;border:1px solid transparent}
    .msg.ok{border-color:rgba(22,163,74,.45);background:rgba(22,163,74,.16);color:rgb(134,239,172)}
    .msg.err{border-color:rgba(225,29,72,.45);background:rgba(225,29,72,.16);color:rgb(251,113,133)}
    :host-context(body.light) .card{background:rgba(255,255,255,.95);border-color:rgba(15,23,42,.10)}
    :host-context(body.light) .hero{
      background:
        radial-gradient(560px 180px at -8% -24%, rgba(13,148,136,.14), transparent 58%),
        radial-gradient(500px 180px at 108% -30%, rgba(2,132,199,.14), transparent 56%),
        rgba(248,250,252,.96);
      border-color:rgba(2,132,199,.20);
    }
    :host-context(body.light) .title,:host-context(body.light) h1{color:#0f172a}
    :host-context(body.light) label,:host-context(body.light) .tagline{color:#475569}
    :host-context(body.light) input{background:#fff;color:#0f172a;border-color:rgba(15,23,42,.12)}
  `]
})
export class LoginPageComponent {
  orgId = '';
  password = '';
  showPassword = signal(false);
  error = signal('');
  success = signal('');
  loading = signal(false);

  constructor(private auth: AuthService, private router: Router) {}

  async signIn(): Promise<void> {
    if (this.loading()) return;
    this.error.set('');
    this.success.set('');

    const username = this.orgId.trim();
    const password = this.password.trim();
    if (!username && !password) {
      this.error.set('Please enter username and password.');
      return;
    }
    if (!username) {
      this.error.set('Please enter username.');
      return;
    }
    if (!password) {
      this.error.set('Please enter password.');
      return;
    }

    this.loading.set(true);
    const ok = await this.auth.login(username, password);
    this.loading.set(false);
    if (!ok) {
      this.error.set('Invalid chatbot number/username or password.');
      return;
    }
    this.router.navigateByUrl('/inbox');
  }

  togglePasswordVisibility(): void {
    this.showPassword.set(!this.showPassword());
  }
}
